/**
 * InstaladorCard — Descarga del instalador vigente y del manual en PDF de un programa que se instala
 * en Windows: Clobi Conector o POS Clobi. Los dos se publican con el mismo mecanismo y se bajan solo
 * desde Clobi, con permiso (no hay enlace público).
 */
import { useState, type ReactNode } from 'react'
import { BookOpen, MonitorDown } from 'lucide-react'
import { Button } from '@/components/Button'
import { fmtDateTime } from '@/lib/utils'
import { downloadInstaller, downloadInstallerManual, useInstallerRelease, type Programa } from '../hooks/usePos'

function megas(bytes: number) {
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`
}

interface Props {
  programa: Programa
  titulo: string
  /** Los pasos para dejar un equipo funcionando. */
  pasos: ReactNode[]
  /** Una línea de ayuda debajo de los pasos. */
  nota: ReactNode
  onError: (e: unknown) => void
}

export default function InstaladorCard({ programa, titulo, pasos, nota, onError }: Props) {
  const { data: release, isLoading } = useInstallerRelease(programa)
  const [bajando, setBajando] = useState(false)
  const [bajandoManual, setBajandoManual] = useState(false)
  if (isLoading) return null

  const descargarManual = async () => {
    setBajandoManual(true)
    try {
      await downloadInstallerManual(programa)
    } catch (e) {
      onError(e)
    } finally {
      setBajandoManual(false)
    }
  }

  const descargar = async () => {
    if (!release) return
    setBajando(true)
    try {
      await downloadInstaller(programa, release)
    } catch (e) {
      onError(e)
    } finally {
      setBajando(false)
    }
  }

  return (
    <section className="rounded-xl border border-divider bg-surface p-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-content">{titulo}</h2>
          <ol className="mt-2 text-sm text-content-muted list-decimal pl-5 space-y-1">
            {pasos.map((paso, i) => <li key={i}>{paso}</li>)}
          </ol>
          <p className="mt-2 text-xs text-content-subtle">{nota}</p>
        </div>
        <div className="shrink-0 sm:text-right">
          {release ? (
            <>
              <Button onClick={descargar} disabled={bajando}>
                <MonitorDown size={14} /> {bajando ? 'Descargando…' : 'Descargar instalador'}
              </Button>
              <p className="mt-1 text-xs text-content-subtle" title={`SHA-256 ${release.sha256}`}>
                Versión {release.version} · {megas(release.size)} · {fmtDateTime(release.published_at)}
              </p>
            </>
          ) : (
            <p className="text-sm text-content-subtle">Todavía no hay una versión publicada.</p>
          )}
          <Button variant="secondary" size="sm" className="mt-3" onClick={descargarManual} disabled={bajandoManual}>
            <BookOpen size={14} /> {bajandoManual ? 'Descargando…' : 'Manual de instalación y uso (PDF)'}
          </Button>
        </div>
      </div>
    </section>
  )
}
